import React from 'react';
import { CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';

const OverviewTab = ({ collegesByTier, onTabChange, studentProfile }) => {
  // Calculate portfolio health score
  const calculatePortfolioHealth = () => {
    const reachCount = collegesByTier.reach.length;
    const targetCount = collegesByTier.target.length;
    const safetyCount = collegesByTier.safety.length;

    const totalSchools = reachCount + targetCount + safetyCount;
    const idealTotal = 12;

    // Each tier ideally has 4 schools
    const reachScore = Math.min((reachCount / 4) * 100, 100);
    const targetScore = Math.min((targetCount / 4) * 100, 100);
    const safetyScore = Math.min((safetyCount / 4) * 100, 100);

    // Average of all three tiers
    const averageScore = Math.round((reachScore + targetScore + safetyScore) / 3);

    return {
      score: averageScore,
      totalSchools,
      isBalanced: reachCount >= 3 && targetCount >= 3 && safetyCount >= 3
    };
  };

  const portfolioHealth = calculatePortfolioHealth();

  const tierData = [
    {
      tier: 'reach',
      label: 'Reach Tier',
      count: collegesByTier.reach.length,
      target: 4,
      color: {
        bg: 'bg-white',
        border: 'border-coral-300',
        text: 'text-coral-400',
        progressBg: 'bg-coral-200',
        progressFill: 'bg-coral-300'
      }
    },
    {
      tier: 'target',
      label: 'Target Tier',
      count: collegesByTier.target.length,
      target: 4,
      color: {
        bg: 'bg-white',
        border: 'border-teal-400',
        text: 'text-teal-400',
        progressBg: 'bg-teal-50',
        progressFill: 'bg-teal-300'
      }
    },
    {
      tier: 'safety',
      label: 'Safety Tier',
      count: collegesByTier.safety.length,
      target: 4,
      color: {
        bg: 'bg-white',
        border: 'border-gold-400',
        text: 'text-gold-400',
        progressBg: 'bg-gold-50',
        progressFill: 'bg-gold-300'
      }
    }
  ];

  // Determine next step recommendation
  const getNextStepRecommendation = () => {
    const incomplete = tierData.find(t => t.count < t.target);

    if (!incomplete) {
      return {
        tier: null,
        message: "Your portfolio is complete and well-balanced! Consider reviewing your AI tier analyses for optimization opportunities."
      };
    }

    const needed = incomplete.target - incomplete.count;
    const tierName = incomplete.label.replace(' Tier', '');

    return {
      tier: incomplete.tier,
      message: `Add ${needed} more ${tierName} school${needed > 1 ? 's' : ''} to complete your balanced portfolio. Based on your profile, we recommend exploring schools that align with your academic interests and geographic preferences.`
    };
  };

  const nextStep = getNextStepRecommendation();

  return (
    <div>
      {/* Overall Portfolio Status */}
      <div className="bg-white border-2 border-slate-900 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-slate-900 mb-1">Overall Portfolio Status</h3>
            <p className="text-sm text-slate-600">
              Your {portfolioHealth.totalSchools}-school portfolio is {portfolioHealth.isBalanced ? 'well-balanced' : 'developing'}
            </p>
          </div>
          <div className="bg-teal-50 border-2 border-slate-900 rounded-lg px-4 py-2">
            <div className="text-xs font-semibold text-teal-800 mb-0.5">Portfolio Health</div>
            <div className="text-2xl font-bold text-teal-800">{portfolioHealth.score}%</div>
          </div>
        </div>

        {/* Tier Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {tierData.map(({ tier, label, count, target, color }) => {
            const percentage = Math.round((count / target) * 100);
            const isComplete = count >= target;
            const needed = target - count;

            return (
              <div key={tier} className={`${color.bg} border ${color.border} rounded-lg p-4`}>
                <div className={`text-sm font-semibold ${color.text} mb-1`}>{label}</div>
                <div className="flex items-center justify-between">
                  <div className={`text-2xl font-bold ${color.text}`}>{count}/{target}</div>
                  <div className="text-xs text-slate-900 font-semibold">
                    {isComplete ? '✓ Complete' : `⚠ ${needed} needed`}
                  </div>
                </div>
                <div className={`h-2 ${color.progressBg} rounded-full overflow-hidden mt-2`}>
                  <div
                    className={`h-full ${color.progressFill}`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Next Step Banner */}
        <div className="bg-azure-50 border-l-4 border-azure-500 rounded-r-lg p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">🎯</div>
            <div className="flex-1">
              <div className="font-semibold text-azure-900 mb-1 text-sm">Next Step</div>
              <div className="text-sm text-slate-700 mb-2">
                {nextStep.message}
              </div>
              {nextStep.tier && (
                <button
                  onClick={() => onTabChange(nextStep.tier)}
                  className="text-sm font-semibold text-azure-600 hover:text-azure-700 flex items-center gap-1"
                >
                  Explore Recommended {tierData.find(t => t.tier === nextStep.tier)?.label.replace(' Tier', '')}s
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
